import React, { forwardRef } from 'react';
import { AnimatePresence } from 'motion/react';
import { useKeyboardShortcut } from 'src/hooks';
import { useAudioPlayback } from 'src/context/AudioContext';
import { getColors } from './utils';
import * as S from './styles';

/**
 * The current state for the button.
 *
 * Note that this is to be differentiated from the ResponseState in
 * that it refers to the state of the button, not the session.
 * Two adjacent AudioResponseButtons may have different ButtonStates
 * but there can only be one, unified ResponseState value for the session.
 */
export type ButtonState = 'green' | 'red' | 'default' | 'yellow';

type AudioResponseButtonProps = {
  buttonState: ButtonState;
  disabled: boolean;
  handleClick: () => void;
  shortcutNumber: number;
  src: string;
  ariaText?: string;
};

export const AudioResponseButton = forwardRef(function InnerAudioResponseButton(
  { buttonState, disabled, handleClick, shortcutNumber, src, ariaText }: AudioResponseButtonProps,
  ref,
) {
  const { play, playing } = useAudioPlayback(src);

  const onClick = async () => {
    handleClick();

    /* istanbul ignore else: no code run */
    if (buttonState === 'default') {
      await play();
    }
  };

  useKeyboardShortcut(shortcutNumber.toString(), async () => {
    if (disabled) return;
    await onClick();
  });

  return (
    <S.Button
      aria-label={ariaText}
      title={ariaText}
      onClick={onClick}
      $colors={getColors(buttonState, playing)}
      ref={ref as React.RefObject<HTMLButtonElement>}
      disabled={disabled}
      data-testid="audioPlayer"
    >
      <S.Number $colors={getColors(buttonState, playing)}>{shortcutNumber}</S.Number>
      <AnimatePresence>
        {playing && (
          <S.Pulse
            data-testid="AudioResponseButton-Pulse"
            initial={{
              scale: 0,
              opacity: 0,
            }}
            exit={{
              scale: 0,
              opacity: 0,
            }}
            animate={{
              scale: [0.8, 1, 0.8],
              opacity: [0.05, 1, 0.05],
              transition: {
                duration: 0.8,
                repeat: Infinity,
                repeatType: 'loop' as const,
                ease: 'easeInOut',
                repeatDelay: 0.15,
              },
            }}
          />
        )}
      </AnimatePresence>
      <S.SpeakerIcon />
    </S.Button>
  );
});
